import React, { useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import Select from 'react-select';

import css from './FieldMultiSelect.module.css';
import classNames from 'classnames';

const customStyles = {
  control: (styles, state) => ({
    ...styles,
    backgroundColor: '#fff',
    border: state.isFocused ? '1px solid var(--marketplaceColor)' : '1px solid #d8dce6',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(26, 115, 232, 0.2)' : 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--marketplaceColor)' : '#b2b8c5' },
    minHeight: '50px',
    paddingTop: '5px',
    paddingBottom: '5px',
    paddingLeft: '16px',
    paddingRight: '12px',
    margin: '0px',
    position: 'relative',
    fontFamily: "'Inter', sans-serif",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    paddingRight: 0,
    transition: 'all .2s ease',
    fontFamily: "'Inter', sans-serif",
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
  }),
  indicatorSeparator: styles => ({ display: 'none' }),
  valueContainer: (base, state) => ({
    ...base,
    padding: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    margin: 0,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: '4px',
  }),
  input: (base, state) => ({
    ...base,
    margin: 0,
    padding: '2px 4px 2px 6px',
    minHeight: 'auto',
    color: '#000',
  }),
  inputContainer: (base, state) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  multiValue: (base, state) => ({
    ...base,
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
    margin: 0,
    minHeight: 'auto',
    alignItems: 'center',
  }),
  multiValueLabel: (base, state) => ({
    ...base,
    color: '#333',
    fontSize: '13px',
    fontWeight: '400',
    padding: '2px 4px 2px 6px',
    fontFamily: "'Inter', sans-serif",
    lineHeight: '1.3',
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    color: '#666',
    cursor: 'pointer',
    padding: '2px 4px',
    '&:hover': {
      backgroundColor: '#e0e0e0',
      color: '#333',
    },
  }),
  option: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    color: '#000',
    padding: '12px 16px',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    fontFamily: "'Inter', sans-serif",
    backgroundColor: isDisabled ? null : isFocused ? 'var(--colorGrey100)' : isSelected ? '#FFF' : null,
  }),
  singleValue: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    color: '#4a4a4a',
    lineHeight: '118%',
    fontFamily: "'Inter', sans-serif;",
    fontSize: '16px',
    fontWeight: '400',
  }),
  menuPortal: base => ({ ...base, postion: 'absolute', top: '0', left: '0' }),
  menu: base => ({
    ...base,
    borderRadius: '4px',
    marginTop: '8px',
    backgroundColor: '#fff',
    color: '#000',
    fontFamily: "'Inter', sans-serif",
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    backgroundColor: '#fff',
    fontSize: '16px',
    fontWeight: '400',
    fontFamily: "'Inter', sans-serif",
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    color: '#000',
  }),
  placeholder: base => ({
    ...base,
    padding: '6px 10px',
    backgroundColor: '#fff',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    color: '#b2b2b2',
    fontFamily: "'Inter', sans-serif;",
  }),
};

/**
 * A react-select picker in a final-form field. react-select only renders in the browser, so
 * the server's page and the browser's first render show the label and an empty box, and the
 * picker mounts after.
 */
const FieldMultiSelect = props => {
  const {
    id,
    name,
    options,
    label,
    className,
    closeMenuOnSelect,
    hideLabel = false,
    isMulti,
    customOnChange = () => {},
    placeholderText,
    validate,
  } = props;

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const inputId = id || name;

  return options ? (
    <Field name={name} validate={validate}>
      {fieldProps => {
        const { input, meta } = fieldProps;
        const { onChange, value, ...rest } = input;
        const hasError = meta.touched && meta.error;

        // Convert stored IDs back to full objects for react-select display
        const displayValue =
          value && Array.isArray(value) && typeof value[0] === 'string'
            ? value.map(id => options.find(option => option.value === id)).filter(Boolean)
            : value;
        return (
          <div className={className}>
            {label ? (
              <label
                htmlFor={inputId}
                className={classNames({
                  [css.hideLabel]: hideLabel,
                })}
              >
                {label}
              </label>
            ) : null}
            <div className={hasError ? css.error : null}>
              {isMounted ? (
                <Select
                  inputId={inputId}
                  className={css.selectContainer}
                  styles={customStyles}
                  classNamePrefix="react-select"
                  closeMenuOnSelect={closeMenuOnSelect}
                  hideSelectedOptions={isMulti}
                  placeholder={placeholderText}
                  isMulti={isMulti}
                  tabSelectsValue={false}
                  components={{ IndicatorSeparator: () => null }}
                  options={options}
                  value={displayValue}
                  getOptionValue={value => value.value}
                  aria-invalid={hasError ? true : undefined}
                  {...rest}
                  onChange={item => {
                    customOnChange(item);
                    // Extract only the values (IDs) from the selected items
                    const values = item
                      ? Array.isArray(item)
                        ? item.map(option => option.value)
                        : item.value
                      : item;
                    input.onChange(values);
                  }}
                />
              ) : (
                <div className={css.placeholderBox} />
              )}
            </div>
            {hasError ? <span className={css.errorText}>{meta.error}</span> : null}
          </div>
        );
      }}
    </Field>
  ) : null;
};

export default FieldMultiSelect;
